import type { Result } from '@tsagko/label-contracts'
import { err } from '@tsagko/label-contracts'
import type { SessionContext } from '@tsagko/label-contracts'

/**
 * Wire envelope for every postMessage crossing the iframe boundary, per
 * design-spec §6.2 and the embed interface sheet's "postMessage protocol
 * canon". Both sides (this transport + the host's reference module in
 * `packages/host-js`) implement exactly this shape.
 */
export interface PostMessageEnvelope {
  msgId: string
  timestamp: number
  type: string
  payload: unknown
}

/** Payload carried by the host→designer `ld.session` envelope. */
export interface SessionPayload {
  protocolVersion: number
  sessionContext: SessionContext
}

export interface RequestOptions {
  signal?: AbortSignal
  /** Defaults to 30_000ms; callers pass a shorter value for render (20_000ms). */
  timeoutMs?: number
}

export interface EmbeddedTransport {
  /**
   * Sends `<method>.request` to the locked host origin (or, before the
   * handshake locks an origin, to every allowed origin) and resolves with
   * the `<method>.response` payload untouched — that payload IS the
   * `Result<T>` the host computed. On timeout, resolves (does not reject)
   * with a local `Result` error `UPSTREAM_UNAVAILABLE`. If `opts.signal`
   * aborts while the request is in flight, a `<method>.cancel` envelope is
   * sent (fire-and-forget) and the returned promise REJECTS.
   */
  request<T = unknown>(
    method: string,
    payload: unknown,
    opts?: RequestOptions
  ): Promise<Result<T>>
  /** Registers a callback for every valid `ld.session` envelope received. Returns an unsubscribe function. */
  onSession(cb: (session: SessionPayload) => void): () => void
  /** Begins listening for host messages and posts `ld.ready` to every allowed origin. Idempotent. */
  start(): void
  /**
   * Full teardown: removes the window `message` listener, settles every
   * in-flight request with the same local `UPSTREAM_UNAVAILABLE` Result the
   * timeout path produces (resolve, not reject — callers awaiting a
   * `Result` keep their contract), clears all `onSession` subscribers, and
   * resets the origin lock. Idempotent; safe to call before `start()`.
   * A later `start()` re-registers the listener and begins a fresh
   * handshake (posts `ld.ready` to every allowed origin again).
   */
  stop(): void
}

const MAX_ENVELOPE_AGE_MS = 30_000
const DEFAULT_TIMEOUT_MS = 30_000

interface PendingRequest {
  timer: ReturnType<typeof setTimeout>
  resolve: (result: Result<unknown>) => void
  onAbort?: () => void
  signal?: AbortSignal
}

export function createTransport({
  allowedOrigins,
}: {
  allowedOrigins: string[]
}): EmbeddedTransport {
  if (allowedOrigins.length === 0) {
    throw new Error('createTransport: allowedOrigins must not be empty')
  }

  let lockedOrigin: string | null = null
  let started = false
  const sessionCallbacks = new Set<(session: SessionPayload) => void>()
  const pending = new Map<string, PendingRequest>()

  function isAllowedOrigin(origin: string): boolean {
    // A literal "null" origin (opaque origin, e.g. a sandboxed iframe
    // without allow-same-origin, or a spoofed message) is NEVER valid,
    // even if it were somehow present in the allowlist.
    if (origin === 'null') return false
    return allowedOrigins.includes(origin)
  }

  function sendTargets(): string[] {
    return lockedOrigin ? [lockedOrigin] : allowedOrigins
  }

  function makeEnvelope(type: string, payload: unknown): PostMessageEnvelope {
    return { msgId: crypto.randomUUID(), timestamp: Date.now(), type, payload }
  }

  function post(envelope: PostMessageEnvelope, origin: string): void {
    window.parent.postMessage(envelope, origin)
  }

  function broadcast(envelope: PostMessageEnvelope): void {
    for (const origin of sendTargets()) post(envelope, origin)
  }

  function isFresh(timestamp: number): boolean {
    return Date.now() - timestamp <= MAX_ENVELOPE_AGE_MS
  }

  function settle(msgId: string, result: Result<unknown>): void {
    const req = pending.get(msgId)
    if (!req) return
    clearTimeout(req.timer)
    if (req.onAbort && req.signal) req.signal.removeEventListener('abort', req.onAbort)
    pending.delete(msgId)
    req.resolve(result)
  }

  function handleMessage(event: MessageEvent): void {
    const data = event.data as Partial<PostMessageEnvelope> | null
    if (!data || typeof data !== 'object') return
    if (
      typeof data.msgId !== 'string' ||
      typeof data.timestamp !== 'number' ||
      typeof data.type !== 'string'
    ) {
      return
    }
    if (!isAllowedOrigin(event.origin)) return
    if (!isFresh(data.timestamp)) return

    if (data.type === 'ld.session') {
      if (!lockedOrigin) lockedOrigin = event.origin
      // Once locked, only trust further session envelopes from that origin.
      if (event.origin !== lockedOrigin) return
      const payload = data.payload as SessionPayload
      for (const cb of sessionCallbacks) {
        // Exception-isolate each subscriber so one throwing callback cannot
        // starve the ones registered after it.
        try {
          cb(payload)
        } catch (error) {
          console.error('embedded transport: ld.session subscriber threw', error)
        }
      }
      return
    }

    // For every other envelope type, once an origin is locked, ignore
    // anything not coming from it (defends against a second window racing
    // the handshake).
    if (lockedOrigin && event.origin !== lockedOrigin) return

    if (data.type.endsWith('.response')) {
      settle(data.msgId, data.payload as Result<unknown>)
    }
  }

  return {
    start(): void {
      if (started) return
      started = true
      window.addEventListener('message', handleMessage)
      const ready = makeEnvelope('ld.ready', {})
      for (const origin of allowedOrigins) post(ready, origin)
    },

    stop(): void {
      if (started) {
        started = false
        window.removeEventListener('message', handleMessage)
      }
      // Settle (resolve) every in-flight request with the same local error
      // the timeout path produces; settle() also clears timers and abort
      // listeners. Copy the keys — settle() mutates the map.
      for (const msgId of [...pending.keys()]) {
        settle(
          msgId,
          err('UPSTREAM_UNAVAILABLE', 'Transport stopped before a response arrived', {
            recoverable: true,
            retryAfterMs: 3000,
          })
        )
      }
      sessionCallbacks.clear()
      lockedOrigin = null
    },

    onSession(cb: (session: SessionPayload) => void): () => void {
      sessionCallbacks.add(cb)
      return () => sessionCallbacks.delete(cb)
    },

    request<T = unknown>(
      method: string,
      payload: unknown,
      opts?: RequestOptions
    ): Promise<Result<T>> {
      if (opts?.signal?.aborted) {
        return Promise.reject(
          new DOMException(`${method} request aborted before send`, 'AbortError')
        )
      }

      const envelope = makeEnvelope(`${method}.request`, payload)
      const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS

      return new Promise<Result<T>>((resolve, reject) => {
        const timer = setTimeout(() => {
          settle(
            envelope.msgId,
            err('UPSTREAM_UNAVAILABLE', `Timed out waiting for ${method}.response`, {
              recoverable: true,
              retryAfterMs: 3000,
            })
          )
        }, timeoutMs)

        let onAbort: (() => void) | undefined
        if (opts?.signal) {
          onAbort = () => {
            pending.delete(envelope.msgId)
            clearTimeout(timer)
            // Reject BEFORE the fire-and-forget cancel broadcast: a
            // hypothetical postMessage throw must never leave the promise
            // permanently pending.
            reject(new DOMException(`${method} request aborted`, 'AbortError'))
            try {
              broadcast(makeEnvelope(`${method}.cancel`, { msgId: envelope.msgId }))
            } catch (error) {
              console.error('embedded transport: failed to post cancel envelope', error)
            }
          }
          opts.signal.addEventListener('abort', onAbort)
        }

        pending.set(envelope.msgId, {
          timer,
          resolve: resolve as (result: Result<unknown>) => void,
          onAbort,
          signal: opts?.signal,
        })

        broadcast(envelope)
      })
    },
  }
}
